import { useState } from "react";
import {
  extractPlaceholders,
  slugifyVariableName,
  templateVariableNameSchema,
  type TemplateParameterFormat,
} from "@school-sales-ai/shared";

/**
 * School vocabulary, offered as one-click chips.
 *
 * A convenience, NOT a contract: nothing guarantees yet that the send side will
 * have a value for any of these — the admin can add whatever the message needs.
 */
const VARIABLE_SUGGESTIONS = ["nombre", "fecha", "hora", "programa", "campus"] as const;

/**
 * Inserts a variable into the body without the admin ever typing braces.
 *
 * `{{1}}` means nothing to an admissions secretary, and a missing or shifted
 * example is Meta's number-one cause of rejection. Named variables let the body
 * read "Hola {{nombre}}" — but the format is fixed by Meta at CREATION, so a
 * template that already uses `{{1}}` keeps getting numbers here.
 */
export function VariableToolbar({
  format,
  body,
  onInsert,
}: {
  format: TemplateParameterFormat;
  body: string;
  onInsert: (token: string) => void;
  }): React.JSX.Element {
  const [customOpen, setCustomOpen] = useState(false);
  const [custom, setCustom] = useState("");

  if (format === "POSITIONAL") {
    const next = extractPlaceholders(body).length + 1;
    return (
      <div className="mt-2 space-y-1">
        <button
          type="button"
          onClick={() => onInsert(String(next))}
          className="rounded-full border border-ink-200 px-3 py-1 text-xs text-ink-700 hover:bg-ink-050"
        >
          + Insertar dato
        </button>
        <p className="text-xs text-ink-500">
          Esta plantilla usa datos numerados; Meta no permite cambiarlos por datos con
          nombre una vez creada.
        </p>
      </div>
    );
  }

  const slug = slugifyVariableName(custom);
  const customError =
    custom.trim() === "" ? null : (templateVariableNameSchema.safeParse(slug).error?.issues[0]?.message ?? null);

  function addCustom(): void {
    if (custom.trim() === "" || customError) return;
    onInsert(slug);
    setCustom("");
    setCustomOpen(false);
  }

  return (
    <div className="mt-2 space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-ink-500">Insertar dato:</span>
        {VARIABLE_SUGGESTIONS.map((name) => (
          <button
            key={name}
            type="button"
            onClick={() => onInsert(name)}
            className="rounded-full border border-ink-200 px-3 py-1 text-xs capitalize text-ink-700 hover:bg-ink-050"
          >
            + {name}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setCustomOpen((open) => !open)}
          className="rounded-full border border-dashed border-ink-300 px-3 py-1 text-xs text-ink-600 hover:bg-ink-050"
        >
          + Otro dato…
        </button>
      </div>

      {customOpen ? (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={custom}
              autoFocus
              maxLength={60}
              onChange={(e) => setCustom(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addCustom();
                }
              }}
              placeholder="Nombre del asesor"
              aria-label="Nombre del dato"
              className="w-full rounded-md border border-ink-200 px-3 py-1.5 text-sm"
            />
            <button
              type="button"
              onClick={addCustom}
              disabled={custom.trim() === "" || customError !== null}
              className="shrink-0 rounded-md border border-ink-200 px-3 py-1.5 text-xs text-ink-700 disabled:opacity-50"
            >
              Insertar
            </button>
          </div>
          {customError ? (
            <p className="text-xs text-red-600">{customError}</p>
          ) : slug === "" ? null : (
            // Meta stores the slug, so show it rather than let the admin discover
            // the transformation in the body they did not expect.
            <p className="text-xs text-ink-500">
              Se insertará como <code>{`{{${slug}}}`}</code>
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}
